import { useEffect, useRef, useState } from 'react';
import { useGameEngine } from '../../hooks/useGameEngine.js';
import { TerminalShell } from '../terminal/TerminalShell.js';
import { BootSequence } from '../terminal/BootSequence.js';
import { TransmissionAnimation } from '../terminal/TransmissionAnimation.js';
import { MissionContent } from './MissionContent.js';
import { NoMissionYet } from './NoMissionYet.js';
import { WaitingForSignal } from './WaitingForSignal.js';
import { GeoPrompt } from './GeoPrompt.js';
import { FieldNotes } from './FieldNotes.js';
import { SolutionPanel } from './SolutionPanel.js';
import { UrgentBanner } from './UrgentBanner.js';
import { AgentPanel } from '../agent/AgentPanel.js';
import { TopBar } from '../shared/TopBar.js';
import { InstallPrompt } from '../shared/InstallPrompt.js';
import { NotifyPrompt } from '../shared/NotifyPrompt.js';
import { HiddenAdminLink } from '../shared/HiddenAdminLink.js';
import { playSound } from '../../lib/sound.js';
import { markBooted, markOpeningSeen, readIntroSeen } from '../../lib/introSeen.js';

type Phase = 'boot' | 'transmission' | 'content';

export function MissionScreen() {
  const engine = useGameEngine();
  const animationsEnabled = engine.state?.settings.animationsEnabled ?? true;
  const soundEnabled = engine.state?.settings.soundEnabled ?? true;

  // מה כבר הוצג במכשיר הזה (לבקשת המשתמש — כמו שזוכרים התחברות): האתחול רץ פעם אחת
  // בלבד, ואנימציית קבלת משימה רק כשהפתיחה הנוכחית עוד לא הוצגה כאן. כך כניסה חוזרת
  // או מעבר בין מסכים מציגים את המשימה מיד.
  const [seen] = useState(readIntroSeen);
  const [phase, setPhase] = useState<Phase>(seen.booted ? 'content' : 'boot');
  // "SCANNING" עד שהטרמינל מציג בפועל "> סוכן 013 זוהה." בתוך אנימציית ההעברה.
  // כשהזיהוי כבר הוצג במכשיר הזה — נשאר "מזוהה" מההתחלה.
  const [identified, setIdentified] = useState(seen.openingId !== null);
  const shownOpeningId = useRef<string | null>(seen.openingId);

  const handleBootDone = () => {
    markBooted();
    const current = engine.state?.current;
    if (current && current.id !== shownOpeningId.current) {
      setPhase('transmission');
    } else {
      setPhase('content');
    }
  };

  const handleTransmissionDone = () => {
    const openingId = engine.state?.current?.id ?? null;
    shownOpeningId.current = openingId;
    if (openingId) markOpeningSeen(openingId);
    setPhase('content');
    playSound('success', soundEnabled);
  };

  // זיהוי מעבר חי למשימה חדשה בזמן שהאפליקציה פתוחה.
  // תלויים בכוונה רק במזהה הפתיחה הנוכחי (לא בכל engine.state) — אחרת האפקט
  // היה מריץ מחדש את הבדיקה בכל poll, גם כשמשהו אחר במצב השתנה.
  // שימו לב: לא מאפסים identified כאן — הזיהוי נשאר ירוק ברציפות למשך כל
  // חיי האפליקציה (מתאפס רק ברענון/פתיחה מחדש), גם כשעוברים למשימה חדשה.
  const currentOpeningId = engine.state?.current?.id ?? null;
  useEffect(() => {
    if (phase !== 'content') return;
    if (currentOpeningId && currentOpeningId !== shownOpeningId.current) {
      setPhase('transmission');
      playSound('alert', soundEnabled);
    }
  }, [currentOpeningId, phase, soundEnabled]);

  const operationName = engine.state?.settings.operationName ?? 'מבצע בר-מצווש';

  return (
    <div className="app-page">
      <TopBar online={engine.online} operationName={operationName} />
      <div className="mission-layout">
        <AgentPanel animationsEnabled={animationsEnabled} identified={identified} />

        <div className="mission-main">
          {phase === 'content' && <UrgentBanner messages={engine.state?.messages ?? []} />}
          <TerminalShell animationsEnabled={animationsEnabled}>
            <div className="term-panel-header">
              <span>SECURE//TERMINAL</span>
              <span>{engine.online ? 'LINK: ACTIVE' : 'LINK: DOWN'}</span>
            </div>
            <div className="mission-terminal-body">
              {phase === 'boot' && (
                <BootSequence animationsEnabled={animationsEnabled} onDone={handleBootDone} />
              )}

              {phase === 'transmission' && (
                <TransmissionAnimation
                  agentLabel="סוכן 013"
                  animationsEnabled={animationsEnabled}
                  onDone={handleTransmissionDone}
                  onIdentified={() => setIdentified(true)}
                />
              )}

              {phase === 'content' && (
                <>
                  {engine.state?.current ? (
                    // unlockMethod לא מועבר בכוונה — מסך הסוכן לא אמור לחשוף
                    // אם המשימה נפתחה ידנית/אוטומטית, רק ב-History (לבקשת המשתמש)
                    <MissionContent
                      snapshot={engine.state.current.snapshot}
                      openedAt={engine.state.current.openedAt}
                    />
                  ) : engine.loading && !engine.state ? null : (
                    // בזמן הטעינה הראשונית לא מציגים "אין משימה" בטעות
                    <NoMissionYet nextPending={engine.state?.nextPending ?? null} />
                  )}

                  {engine.awaitingSignal && <WaitingForSignal />}

                  {engine.needsLocation && (
                    <GeoPrompt
                      permission={engine.geoPermission}
                      error={engine.geoError}
                      accuracy={engine.geoPosition ? engine.geoPosition.coords.accuracy : null}
                      onRequest={engine.refreshLocation}
                    />
                  )}

                  {engine.lastOpenError && (
                    <div className="term-text term-line term-line--error">
                      &gt; {engine.lastOpenError}
                    </div>
                  )}
                </>
              )}
            </div>
          </TerminalShell>

          {phase === 'content' && engine.state?.solution && (
            <SolutionPanel
              solution={engine.state.solution}
              online={engine.online}
              animationsEnabled={animationsEnabled}
              onChanged={engine.refresh}
            />
          )}

          {phase === 'content' && (
            <FieldNotes
              messages={engine.state?.messages ?? []}
              pendingCount={engine.pendingMessageCount}
              animationsEnabled={animationsEnabled}
              onSend={engine.sendMessage}
            />
          )}

          {phase === 'content' && <NotifyPrompt />}
          <InstallPrompt />
        </div>
      </div>
      <HiddenAdminLink />
    </div>
  );
}
